'use client'

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  BulkCustomerFilesUploader,
  BulkCustomerFileEntry,
} from '@/components/shared/inputComponents/BulkCustomerFilesUploader'

const configuratorSchema = z.object({
  is_configurator_enabled: z.boolean(),
})

const claimsSchema = z.object({
  is_claims_enabled: z.boolean(),
})

const palettesSchema = z.object({
  selectedPalettes: z.array(z.number()),
  action: z.enum(['add', 'replace', 'remove']),
})

const customerFileMetaSchema = z.object({
  name: z.string().min(1),
  storage_key: z.string().min(1),
  original_name: z.string().nullish(),
  mime_type: z.string().nullish(),
  size_bytes: z.number().nullish(),
})

const filesSchema = z.object({
  files: z.array(customerFileMetaSchema).min(1, 'Select at least one file to add'),
})

const bulkEditSchema = z.union([
  configuratorSchema,
  claimsSchema,
  palettesSchema,
  filesSchema,
])

interface BulkEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  selectedCustomers: any[]
  isLoading?: boolean
  selectionMode?: 'individual' | 'all-filtered'
  totalFilteredCount?: number
  editProperty?: 'configurator' | 'claims' | 'palettes' | 'files'
  availablePalettes?: any[]
}

export function BulkEditModal({
  isOpen,
  onClose,
  onSubmit,
  selectedCustomers,
  isLoading = false,
  selectionMode = 'individual',
  totalFilteredCount = 0,
  editProperty = 'configurator',
  availablePalettes = [],
}: BulkEditModalProps) {
  const configuratorForm = useForm({
    resolver: zodResolver(configuratorSchema),
    defaultValues: {
      is_configurator_enabled: false,
    },
  })

  const claimsForm = useForm({
    resolver: zodResolver(claimsSchema),
    defaultValues: {
      is_claims_enabled: false,
    },
  })

  const palettesForm = useForm({
    resolver: zodResolver(palettesSchema),
    defaultValues: {
      selectedPalettes: [] as number[],
      action: 'add' as const,
    },
  })

  const filesForm = useForm({
    resolver: zodResolver(filesSchema),
    defaultValues: {
      files: [] as z.infer<typeof filesSchema>['files'],
    },
  })

  // File upload progress lives here (not inside the uploader component) so it
  // survives being read by the submit-button's disabled state, and so it can
  // be reset from one place regardless of which path closed the dialog.
  const [fileEntries, setFileEntries] = useState<BulkCustomerFileEntry[]>([])

  // Keep the form's `files` value in sync with successfully uploaded entries
  // only — uploading/errored entries never reach the submitted payload.
  // Deliberately not `shouldValidate: true`: the Update button already gates
  // on `canSubmitFiles`, and validating here would also fire on the
  // `setFileEntries([])` reset when the dialog closes, writing a stale
  // "select at least one file" error into a form nobody is looking at yet —
  // it would then render the moment the dialog reopens.
  useEffect(() => {
    const done = fileEntries.filter(entry => entry.status === 'done' && entry.result)
    filesForm.setValue(
      'files',
      done.map(entry => ({
        name: entry.result!.original_name || entry.file.name,
        storage_key: entry.result!.storage_key,
        original_name: entry.result!.original_name,
        mime_type: entry.result!.mime_type,
        size_bytes: entry.result!.size_bytes,
      })),
      { shouldDirty: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileEntries])

  // `isOpen` can flip to false from a path that never calls handleClose (the
  // success handler in B2BCustomers.tsx closes the modal directly), so reset
  // here rather than relying solely on handleClose.
  useEffect(() => {
    if (!isOpen) {
      configuratorForm.reset()
      claimsForm.reset()
      palettesForm.reset()
      filesForm.reset({ files: [] })
      setFileEntries([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleSubmit = (data: any) => {
    onSubmit(data)
  }

  const handleClose = () => {
    configuratorForm.reset()
    claimsForm.reset()
    palettesForm.reset()
    filesForm.reset({ files: [] })
    setFileEntries([])
    onClose()
  }

  const getPropertyTitle = () => {
    switch (editProperty) {
      case 'configurator':
        return 'Configurator Access'
      case 'claims':
        return 'Claims Portal'
      case 'palettes':
        return 'Fabric Palettes'
      case 'files':
        return 'Files'
      default:
        return 'Bulk Edit'
    }
  }

  const isFilesUploading = fileEntries.some(entry => entry.status === 'uploading')
  const isFilesInvalid = fileEntries.some(entry => entry.status === 'error')
  const canSubmitFiles =
    fileEntries.length > 0 && !isFilesUploading && !isFilesInvalid

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{getPropertyTitle()} - Bulk Edit</DialogTitle>
          <DialogDescription>
            {selectionMode === 'all-filtered' ? (
              <>
                Bulk edit {totalFilteredCount} filtered customer
                {totalFilteredCount === 1 ? '' : 's'}
              </>
            ) : (
              <>
                Bulk edit {selectedCustomers.length} selected customer
                {selectedCustomers.length === 1 ? '' : 's'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {editProperty === 'configurator' ? (
          <Form {...configuratorForm}>
            <form
              onSubmit={configuratorForm.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={configuratorForm.control}
                  name="is_configurator_enabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Enable Configurator Access
                        </FormLabel>
                        <FormDescription>
                          Allow customers to access the product configurator
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Updating...
                    </div>
                  ) : selectionMode === 'all-filtered' ? (
                    `Update ${totalFilteredCount} Customer${
                      totalFilteredCount === 1 ? '' : 's'
                    }`
                  ) : (
                    `Update ${selectedCustomers.length} Customer${
                      selectedCustomers.length === 1 ? '' : 's'
                    }`
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : editProperty === 'claims' ? (
          <Form {...claimsForm}>
            <form
              onSubmit={claimsForm.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={claimsForm.control}
                  name="is_claims_enabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Enable Claims Portal
                        </FormLabel>
                        <FormDescription>
                          Allow customers to access the claims portal
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Updating...
                    </div>
                  ) : selectionMode === 'all-filtered' ? (
                    `Update ${totalFilteredCount} Customer${
                      totalFilteredCount === 1 ? '' : 's'
                    }`
                  ) : (
                    `Update ${selectedCustomers.length} Customer${
                      selectedCustomers.length === 1 ? '' : 's'
                    }`
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : editProperty === 'files' ? (
          selectionMode === 'all-filtered' ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive">
                Bulk file attachment only supports individually selected
                customers. Close this dialog, clear "select all filtered", and
                select customer rows individually to attach files.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <Form {...filesForm}>
              <form
                onSubmit={filesForm.handleSubmit(handleSubmit)}
                className="space-y-6"
              >
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-1">
                      Files to add
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      These files are added to every selected customer.
                      Existing files on those customers are not changed,
                      renamed, or removed.
                    </p>
                    <BulkCustomerFilesUploader
                      entries={fileEntries}
                      onEntriesChange={setFileEntries}
                      disabled={isLoading}
                    />
                    {filesForm.formState.errors.files?.message && (
                      <p className="mt-2 text-xs text-destructive">
                        {filesForm.formState.errors.files.message as string}
                      </p>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading || !canSubmitFiles}>
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Updating...
                      </div>
                    ) : (
                      `Update ${selectedCustomers.length} Customer${
                        selectedCustomers.length === 1 ? '' : 's'
                      }`
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )
        ) : (
          <Form {...palettesForm}>
            <form
              onSubmit={palettesForm.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={palettesForm.control}
                  name="action"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Action</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="add">
                            Add palettes to customers
                          </SelectItem>
                          <SelectItem value="replace">
                            Replace customer palettes
                          </SelectItem>
                          <SelectItem value="remove">
                            Remove palettes from customers
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose how to modify the palettes for selected customers
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={palettesForm.control}
                  name="selectedPalettes"
                  render={() => (
                    <FormItem>
                      <div className="mb-4">
                        <FormLabel className="text-base">
                          Fabric Palettes
                        </FormLabel>
                        <FormDescription>
                          Select the palettes to apply to customers
                        </FormDescription>
                      </div>
                      <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                        {availablePalettes.map(palette => (
                          <FormField
                            key={palette.id}
                            control={palettesForm.control}
                            name="selectedPalettes"
                            render={({ field }) => {
                              return (
                                <FormItem
                                  key={palette.id}
                                  className="flex flex-row items-start space-x-3 space-y-0"
                                >
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(
                                        palette.id
                                      )}
                                      onCheckedChange={checked => {
                                        const currentValue = field.value || []
                                        return checked
                                          ? field.onChange([
                                              ...currentValue,
                                              palette.id,
                                            ])
                                          : field.onChange(
                                              currentValue.filter(
                                                value => value !== palette.id
                                              )
                                            )
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {palette.name}
                                  </FormLabel>
                                </FormItem>
                              )
                            }}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Updating...
                    </div>
                  ) : selectionMode === 'all-filtered' ? (
                    `Update ${totalFilteredCount} Customer${
                      totalFilteredCount === 1 ? '' : 's'
                    }`
                  ) : (
                    `Update ${selectedCustomers.length} Customer${
                      selectedCustomers.length === 1 ? '' : 's'
                    }`
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
